import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Card, Button } from 'react-bootstrap'
import { Link, useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'

import callAPI from '../../../helpers/callAPI'
import Dollars from '../../../templates/Dollars/Dollars'
import Loading from '../../../templates/UI/Loading/Loading'
import { SystemMessageContext } from '../../../context/SystemMessageContext'
import ReactTooltip from 'react-tooltip'
import AdministratorMarginDetailsModal from '../AdministratorMarginDetailsReport/AdministratorMarginDetailsModal'
import { useAuth, useProtectedPage } from '../../../context/AuthContext'

const TABLE_COLUMN_COUNT = 10
const MIN_REPORT_YEAR = 2026

const MONTH_OPTIONS = [
	{ value: 1, label: 'January' },
	{ value: 2, label: 'February' },
	{ value: 3, label: 'March' },
	{ value: 4, label: 'April' },
	{ value: 5, label: 'May' },
	{ value: 6, label: 'June' },
	{ value: 7, label: 'July' },
	{ value: 8, label: 'August' },
	{ value: 9, label: 'September' },
	{ value: 10, label: 'October' },
	{ value: 11, label: 'November' },
	{ value: 12, label: 'December' },
]

const QUARTER_OPTIONS = [
	{ value: 1, label: 'Q1' },
	{ value: 2, label: 'Q2' },
	{ value: 3, label: 'Q3' },
	{ value: 4, label: 'Q4' },
]

const PerformanceManagementReport = () => {

	const auth = useAuth()
	useProtectedPage(auth.permissions, 'performance_management_report')

	const { showSystemMessage } = useContext(SystemMessageContext)
	const location = useLocation()
	const history = useHistory()
	const currentDate = new Date()
	const currentYear = currentDate.getFullYear()
	const currentMonth = currentDate.getMonth() + 1
	const currentQuarter = Math.ceil(currentMonth / 3)
	const urlParams = queryString.parse(location?.search || '')

	const parsedYear = Number(urlParams.year)
	const initialYear = Number.isInteger(parsedYear) && parsedYear >= MIN_REPORT_YEAR && parsedYear <= currentYear ? parsedYear : currentYear

	const parsedQuarter = Number(urlParams.quarter)
	const hasValidQuarter = Number.isInteger(parsedQuarter) && parsedQuarter >= 1 && parsedQuarter <= 4

	const parsedMonth = Number(urlParams.month)
	const hasValidMonth = Number.isInteger(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12

	const initialPeriodType = hasValidQuarter && !hasValidMonth ? 'quarter' : 'month'
	const initialMonth = hasValidMonth ? parsedMonth : currentMonth
	const initialQuarter = hasValidQuarter ? parsedQuarter : currentQuarter

	const [selectedYear, setSelectedYear] = useState(initialYear)
	const [selectedMonth, setSelectedMonth] = useState(initialMonth)
	const [selectedQuarter, setSelectedQuarter] = useState(initialQuarter)
	const [periodType, setPeriodType] = useState(initialPeriodType)
	const [appliedYear, setAppliedYear] = useState(initialYear)
	const [appliedMonth, setAppliedMonth] = useState(initialPeriodType === 'month' ? initialMonth : null)
	const [appliedQuarter, setAppliedQuarter] = useState(initialPeriodType === 'quarter' ? initialQuarter : null)
	const [appliedPeriodType, setAppliedPeriodType] = useState(initialPeriodType)
	const [refreshToken, setRefreshToken] = useState(0)
	const [dataLoaded, setDataLoaded] = useState(false)
	const [data, setData] = useState(null)
	const [modalData, setModalData] = useState({ open: false, marginId: null, year: null, quarter: null, mode: 'all' })

	useEffect(() => {
		document.title = 'Performance Management Report'
	}, [])

	useEffect(() => {
		setSelectedYear(initialYear)
		setSelectedMonth(initialMonth)
		setSelectedQuarter(initialQuarter)
		setPeriodType(initialPeriodType)
		setAppliedYear(initialYear)
		setAppliedMonth(initialPeriodType === 'month' ? initialMonth : null)
		setAppliedQuarter(initialPeriodType === 'quarter' ? initialQuarter : null)
		setAppliedPeriodType(initialPeriodType)
	}, [initialYear, initialMonth, initialQuarter, initialPeriodType])

	const yearOptions = useMemo(() => {
		const options = []

		for (let year = currentYear; year >= MIN_REPORT_YEAR; year -= 1) {
			options.push(year)
		}

		return options
	}, [currentYear])

	const apiUrl = `reports/performancemanagementreport?${queryString.stringify(
		appliedPeriodType === 'quarter'
			? { year: appliedYear, quarter: appliedQuarter }
			: { year: appliedYear, month: appliedMonth }
	)}`

	useEffect(() => {
		let isMounted = true

		const fetchReport = async () => {
			if (isMounted) {
				setDataLoaded(false)
			}

			const result = await callAPI(apiUrl, 'GET')

			if (!isMounted) {
				return
			}

			if (result?.success) {
				setData(result)
			} else {
				setData(null)
				if (showSystemMessage) {
					showSystemMessage('error', result?.errors?.message || 'Could not load performance management report data.')
				}
			}

			setDataLoaded(true)
		}

		fetchReport()

		return () => {
			isMounted = false
		}
	}, [apiUrl, refreshToken, showSystemMessage])

	const rows = Array.isArray(data?.report) ? data.report : []
	const isQuarterView = appliedPeriodType === 'quarter'

	const toSafeNumber = (value) => {
		const parsedValue = Number.parseFloat(value)
		return Number.isFinite(parsedValue) ? parsedValue : 0
	}
	const sumBy = (getValue) => rows.reduce((total, row) => total + toSafeNumber(getValue(row)), 0)

	const existing_biz_margin = sumBy((row) => row.existing_biz_margin)
	const new_biz_margin = sumBy((row) => row.new_biz_margin)
	const total_margin = sumBy((row) => row.total_margin)
	const target_amount = sumBy((row) => row.target_amount)
	const pacing_to = sumBy((row) => row.pacing_to)
	const qualified_pitches = sumBy((row) => row.qualified_pitches)
	const qualifying_pitches = sumBy((row) => row.qualifying_pitches)
	const total_pitches = sumBy((row) => row.total_pitches)

	const totals = {
		existing_biz_margin,
		new_biz_margin,
		total_margin,
		target_amount,
		pacing_to,
		qualified_pitches,
		qualifying_pitches,
		total_pitches,
		close_rate: qualifying_pitches > 0 ? (qualified_pitches / qualifying_pitches) * 100 : 0,
		pacing_ratio: target_amount > 0 ? (pacing_to / target_amount) * 100 : 0,
	}

	const handleYearChange = (event) => {
		const nextYear = Number(event.target.value)
		setSelectedYear(nextYear)
	}

	const handlePeriodTypeChange = (nextType) => {
		setPeriodType(nextType)
	}

	const handleMonthChange = (event) => {
		const nextMonth = Number(event.target.value)
		setSelectedMonth(nextMonth)
		setPeriodType('month')
	}

	const handleQuarterChange = (event) => {
		const nextQuarter = Number(event.target.value)
		setSelectedQuarter(nextQuarter)
		setPeriodType('quarter')
	}

	const handleRefreshReport = () => {
		setAppliedYear(selectedYear)
		setAppliedPeriodType(periodType)

		if (periodType === 'quarter') {
			setAppliedQuarter(selectedQuarter)
			setAppliedMonth(null)
			setRefreshToken((currentValue) => currentValue + 1)
			history.push(`?${queryString.stringify({
				year: selectedYear,
				quarter: selectedQuarter,
			})}`)
		} else {
			setAppliedMonth(selectedMonth)
			setAppliedQuarter(null)
			setRefreshToken((currentValue) => currentValue + 1)
			history.push(`?${queryString.stringify({
				year: selectedYear,
				month: selectedMonth,
			})}`)
		}
	}

	const openMarginModal = (row, mode) => {
		if (isQuarterView) {
			setModalData({
				open: true,
				marginId: row.admin_id,
				year: appliedYear,
				quarter: appliedQuarter,
				mode,
			})
			return
		}

		setModalData({
			open: true,
			marginId: row.margin_id,
			year: null,
			quarter: null,
			mode,
		})
	}

	const marginDetailsPath = (row, mode) => {
		if (isQuarterView) {
			return `/reports/administratorMarginDetailsReport/${row.admin_id}?${queryString.stringify({
				year: appliedYear,
				quarter: appliedQuarter,
				mode,
			})}`
		}

		return `/reports/administratorMarginDetailsReport/${row.margin_id}?mode=${mode}`
	}

	return (
		<React.Fragment>
			<div className="page-header d-flex align-items-center">
				<span className="mb-h1">Performance Management Report</span>
			</div>

			<div className="row">
				<div className="col-xl-9">
					<AdministratorMarginDetailsModal modalData={modalData} setModalData={setModalData} />
					<Card>
						<Card.Header className="d-flex align-items-center justify-content-start flex-wrap">
							<div className="d-flex align-items-center mr-2 mb-1 mt-1">
								<label className="mb-0 mr-2" htmlFor="performance-report-year">Year</label>
								<select 
									id="performance-report-year" 
									className="form-control form-control-sm mr-3" 
									value={selectedYear} 
									onChange={handleYearChange}
								>
									{yearOptions.map((year) => (
										<option key={year} value={year}>{year}</option>
									))}
								</select>

								<label className="mb-0 mr-2" htmlFor="performance-report-month">Month</label>
								<select
									id="performance-report-month"
									className="form-control form-control-sm mr-3"
									style={{ width: 'auto', minWidth: '7rem' }}
									value={selectedMonth}
									onChange={handleMonthChange}
									disabled={periodType === 'quarter'}
								>
									{MONTH_OPTIONS.map((month) => (
										<option key={month.value} value={month.value}>{month.label}</option>
									))}
								</select>

								<label className="mb-0 mr-3" htmlFor="performance-report-quarter">Quarter</label>
								<select
									id="performance-report-quarter"
									className="form-control form-control-sm mr-3"
									value={selectedQuarter}
									onChange={handleQuarterChange}
									disabled={periodType === 'month'}
								>
									{QUARTER_OPTIONS.map((quarter) => (
										<option key={quarter.value} value={quarter.value}>{quarter.label}</option>
									))}
								</select>

								<input
									type="radio"
									id="performance-report-period-month"
									name="performance-report-period"
									className="mr-1"
									checked={periodType === 'month'}
									onChange={() => handlePeriodTypeChange('month')}
								/>
								<label className="mb-0 mr-3 text-nowrap" htmlFor="performance-report-period-month">By month</label>
								<input
									type="radio"
									id="performance-report-period-quarter"
									name="performance-report-period"
									className="mr-1"
									checked={periodType === 'quarter'}
									onChange={() => handlePeriodTypeChange('quarter')}
								/>
								<label className="mb-0 mr-2 text-nowrap" htmlFor="performance-report-period-quarter">By quarter</label>

								<Button className="ml-2 text-nowrap" onClick={handleRefreshReport} size="sm" style={{ whiteSpace: 'nowrap' }}>
									Refresh Report
								</Button>
							</div>
						</Card.Header>

						<Card.Body className="p-0">
							{!dataLoaded ? (
								<table className="table table-striped table-card table-hover mb-0">
									<tbody>
										<tr>
											<td colSpan={TABLE_COLUMN_COUNT}>
												<Loading />
											</td>
										</tr>
									</tbody>
								</table>
							) : rows.length === 0 ? (
								<table className="table table-striped table-card table-hover mb-0">
									<tbody>
										<tr>
											<td colSpan={TABLE_COLUMN_COUNT}>No stats for this date range.</td>
										</tr>
									</tbody>
								</table>
							) : (
								<table className="table table-striped table-card table-hover mb-0 text-small">
									<thead>
										<ReactTooltip />
										<tr>
											<th>Admin</th>
											<th className="text-right" data-tip={isQuarterView
												? 'Margin earned within the quarter that was not due to a pitch being made - or if a pitch was made, then the pitch did not qualify as it did not meet the criteria for new business.'
												: 'Margin earned within the month that was not due to a pitch being made - or if a pitch was made, then the pitch did not qualify as it did not meet the criteria for new business.'
											}>Existing Margin</th>
											<th className="text-right" data-tip={isQuarterView
												? 'Margin earned within the quarter that can be directly attributed to a pitch being made.'
												: 'Margin earned within the month that can be directly attributed to a pitch being made.'
											}>New Margin</th>
											<th className="text-right">Total Margin</th>
											<th className="text-right border-left">Target</th>
											<th className="text-right">Pacing</th>
											<th className="text-right"></th>
											<th className="text-right border-left" data-tip={isQuarterView
												? 'A count of pitches that met the criteria for new business and resulted in margin earned in the quarter.'
												: 'A count of pitches that met the criteria for new business and resulted in margin earned in the month.'
											}>Closed Pitches</th>
											<th className="text-right" data-tip={isQuarterView
												? 'A count of distinct pitches (by affiliate/offer) made in this quarter + the trailing 3 months.'
												: 'A count of distinct pitches (by affiliate/offer) made this month + the trailing 3 months.'
											}>Qualifying Pitches</th>
											<th className="text-right border-left" data-tip={'Closed Pitches / Qualifying Pitches'}>Close Rate</th>
											<th className="text-right border-left">{isQuarterView ? 'Total Pitches This Quarter' : 'Total Pitches This Month'}</th>
										</tr>
									</thead>
									<tbody>
										{rows.map((row, index) => (
											<tr key={`${row.margin_id || 'q'}-${row.admin_id}-${index}`}>
												<td>{row.full_name || ''}</td>
												<td className="text-right">
													<Link	className="btn btn-sm btn-link p-0 align-baseline"
															to={marginDetailsPath(row, 'existing')}
															onClick={(e) => { e.preventDefault(); openMarginModal(row, 'existing') }}>
														<Dollars value={row.existing_biz_margin} />
													</Link>
												</td>
												<td className="text-right">
													<Link	className="btn btn-sm btn-link p-0 align-baseline"
															to={marginDetailsPath(row, 'new')}
															onClick={(e) => { e.preventDefault(); openMarginModal(row, 'new') }}>
														<Dollars value={row.new_biz_margin} />
													</Link>
												</td>
												<td className="text-right">
													{(isQuarterView || row.margin_id) ? (
														<Link	className="btn btn-sm btn-link p-0 align-baseline"
																to={marginDetailsPath(row, 'all')}
																onClick={(e) => { e.preventDefault(); openMarginModal(row, 'all') }}>
															<Dollars value={row.total_margin} />
														</Link>
													) : (
														<Dollars value={row.total_margin} />
													)}
												</td>
												<td className="text-right border-left">
													<Dollars value={row.target_amount} />
												</td>
												<td className="text-right"><Dollars value={row.pacing_to} /></td>
												<td className={`text-right ${row.pacing_ratio < 100 ? 'text-danger' : 'text-success'}`}>({Number(row.pacing_ratio || 0).toLocaleString('EN-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2	})}%)</td>
												{/* <td className="text-right border-left">{Number(row.disqualified_pitches || 0).toLocaleString()}</td> */}
												<td className="text-right border-left">{Number(row.qualified_pitches || 0).toLocaleString()}</td>
												<td className="text-right">{Number(row.qualifying_pitches || 0).toLocaleString()}</td>
												<td className="text-right border-left">{Number(row.close_rate || 0).toLocaleString('EN-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2	})}%</td>
												<td className="text-right border-left">{Number(row.total_pitches || 0).toLocaleString()}</td>
											</tr>
										))}
									</tbody>
									<tfoot>
										<tr className="font-weight-bold">
											<td>Totals</td>
											<td className="text-right"><Dollars value={totals.existing_biz_margin} /></td>
											<td className="text-right"><Dollars value={totals.new_biz_margin} /></td>
											<td className="text-right"><Dollars value={totals.total_margin} /></td>
											<td className="text-right border-left"><Dollars value={totals.target_amount} /></td>
											<td className="text-right"><Dollars value={totals.pacing_to} /></td>
											<td className={`text-right ${totals.pacing_ratio < 100 ? 'text-danger' : 'text-success'}`}>({Number(totals.pacing_ratio || 0).toLocaleString('EN-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}%)</td>
											<td className="text-right border-left">{Number(totals.qualified_pitches || 0).toLocaleString()}</td>
											<td className="text-right">{Number(totals.qualifying_pitches || 0).toLocaleString()}</td>
											<td className="text-right border-left">{Number(totals.close_rate || 0).toLocaleString('EN-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}%</td>
											<td className="text-right border-left">{Number(totals.total_pitches || 0).toLocaleString()}</td>
										</tr>
									</tfoot>
								</table>
							)}
						</Card.Body>
					</Card>
				</div>
			</div>
		</React.Fragment>
	)
}

export default PerformanceManagementReport